import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { merge, of, Subscription } from 'rxjs';
import { filter, map, take, takeUntil, delay } from 'rxjs/operators';
import { BaseComponent } from '../base-component/base.component';
import { DOCUMENT } from '@angular/common';
import { castBoolean } from '@shared/utils/cast-boolean.util';

@Component({
  selector: 'app-split-value-field',
  templateUrl: './split-value-field.component.html',
  styleUrls: ['./split-value-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SplitValueFieldComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class SplitValueFieldComponent extends BaseComponent
  implements ControlValueAccessor, OnChanges
{
  @Input() length: number;
  @Input() theme: 'outline' | 'fill' = 'outline';
  @Input() set rectangle(value: boolean | string) {
    this.rectangleInputs = castBoolean(value);
  }

  @ViewChildren('fields') fields: QueryList<ElementRef<HTMLElement>>;
  form: FormGroup<{ split: FormArray }>;

  get controls(): AbstractControl[] {
    return this.form?.controls.split.controls || [];
  }

  get maxWidth() {
    if (!this.rectangleInputs) {
      return 'auto';
    }
    const padding = (this.length - 1) * 0.5;
    const width = this.length * 2.25 + padding;
    return `${width}rem`;
  }

  // uuid = Math.random().toString(36).substring(2) + Date.now().toString(36);

  formSubscription: Subscription;
  inputsSubscription: Subscription;
  value: string;
  rectangleInputs = false;
  activeField = 0;

  // private destroy$ = new Subject();
  // eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/no-unused-vars
  onChange = (v: string): void => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  constructor(
    private formBuilder: FormBuilder,
    @Inject(DOCUMENT) private document: Document,
    private changeDetectorRef: ChangeDetectorRef // private injector: Injector
  ) {
    super()
  }

  ngOnChanges({ length }: SimpleChanges): void {
    if (length?.currentValue !== length?.previousValue) {
      this.initForm();
    }
  }

  initForm() {
    this.form = this.formBuilder.group({
      split: this.formBuilder.array(
        Array.from({ length: this.length }, () => this.formBuilder.control(''))
      ),
    });
    this.handleValueChanges();
    this.handleMovingToInputs();
    // this.handleErrorState();
  }

  writeValue(value: string): void {
    if (!this.form) {
      this.initForm();
    }
    this.form.patchValue(
      { split: value?.toString()?.split('') || [] },
      {
        onlySelf: true,
        emitEvent: false,
      }
    );
    this.activeField = value?.length  ;
    this.value = value || '';
    this.changeDetectorRef.markForCheck();
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  handleValueChanges(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    this.formSubscription = this.form.valueChanges.subscribe(
      ({ split: value }) => {
        const parsedValue = value.join('');
        this.onChange(parsedValue);
        this.onTouched();
        this.value = value.join('');
        this.changeDetectorRef.markForCheck();
      }
    );
  }

  handleMovingToInputs(): void {
    if (this.inputsSubscription) {
      this.inputsSubscription.unsubscribe();
    }
    this.inputsSubscription = merge(
      ...(this.form.controls.split as FormArray).controls.map((control, i) =>
        control.valueChanges.pipe(
          filter((v) => !!v),
          map(() => i)
        )
      )
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((index) => {
        this.moveToNextInput(index + 1);
      });
  }

  onBlur() {
    of(null)
      .pipe(delay(500), take(1))
      .subscribe(() => {
        const element = this.document.activeElement;
        const isInternalField = this.fields
          .toArray()
          .find((ref) => ref.nativeElement === element);
        if (!isInternalField) {
          this.onTouched();
        }
      });
  }

  moveToNextInput(index: number): void {
    if (this.fields) {
      const elements = this.fields.toArray();
      if (elements.length) {
        const element = elements[index];
        element?.nativeElement.focus();
        this.activeField = index;
      }
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    event.stopPropagation();
    const pasteText = event.clipboardData?.getData('text') || '';
    this.form.patchValue({
      split: pasteText.split('').slice(0, this.length),
    });
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Backspace') {
      this.moveToNextInput(Math.max(0, this.activeField - 1));
    }
  }
}
